export interface ConfigMataMataPersist {
  melhoresTerceiros: Record<number, string>;
  cruzamentos16: Record<string, { ladoA?: string; ladoB?: string }>;
  liberadoParaPalpites: boolean;
}

export const STORAGE_KEY_MM_CONFIG = 'copa-mm-config-ade';
export const COPA_IDB_KEY_MM_CONFIG = 'mmConfig';

export function configMMVazia(): ConfigMataMataPersist {
  return {
    melhoresTerceiros: {},
    cruzamentos16: {},
    liberadoParaPalpites: false,
  };
}

export function normalizarConfigMM(p?: Partial<ConfigMataMataPersist> | null): ConfigMataMataPersist {
  const vazio = configMMVazia();
  if (!p || typeof p !== 'object') return vazio;
  return {
    melhoresTerceiros:
      p.melhoresTerceiros && typeof p.melhoresTerceiros === 'object'
        ? (p.melhoresTerceiros as Record<number, string>)
        : {},
    cruzamentos16:
      p.cruzamentos16 && typeof p.cruzamentos16 === 'object'
        ? (p.cruzamentos16 as Record<string, { ladoA?: string; ladoB?: string }>)
        : {},
    liberadoParaPalpites: Boolean(p.liberadoParaPalpites),
  };
}

export function contarSlotsMelhoresTerceiros(mt: Record<number, string>): number {
  return [1, 2, 3, 4, 5, 6, 7, 8].filter((s) => Boolean(mt[s])).length;
}

export function contarCruzamentos16(c: Record<string, { ladoA?: string; ladoB?: string }>): number {
  return Object.keys(c || {}).length;
}

/** Prefere a fonte com mais slots manuais preenchidos (nunca descarta escolha manual). */
export function mesclarConfigMM(a: ConfigMataMataPersist, b: ConfigMataMataPersist): ConfigMataMataPersist {
  const slotsA = contarSlotsMelhoresTerceiros(a.melhoresTerceiros);
  const slotsB = contarSlotsMelhoresTerceiros(b.melhoresTerceiros);
  const cruzA = contarCruzamentos16(a.cruzamentos16);
  const cruzB = contarCruzamentos16(b.cruzamentos16);

  const base = slotsA > slotsB || (slotsA === slotsB && cruzA >= cruzB) ? a : b;
  const outro = base === a ? b : a;

  return {
    melhoresTerceiros: { ...outro.melhoresTerceiros, ...base.melhoresTerceiros },
    cruzamentos16: { ...outro.cruzamentos16, ...base.cruzamentos16 },
    liberadoParaPalpites: a.liberadoParaPalpites || b.liberadoParaPalpites,
  };
}

export function carregarConfigMMLocal(): ConfigMataMataPersist {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MM_CONFIG);
    if (!raw) return configMMVazia();
    return normalizarConfigMM(JSON.parse(raw) as Partial<ConfigMataMataPersist>);
  } catch {
    return configMMVazia();
  }
}

export function salvarConfigMMLocal(config: ConfigMataMataPersist): void {
  try {
    localStorage.setItem(STORAGE_KEY_MM_CONFIG, JSON.stringify(config));
  } catch {
    /* quota */
  }
}

const COPA_IDB_NAME = 'ade-copa-db';
const COPA_IDB_VERSION = 1;
const COPA_IDB_STORE = 'copaEstado';

function abrirCopaIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB indisponível'));
      return;
    }
    const req = indexedDB.open(COPA_IDB_NAME, COPA_IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(COPA_IDB_STORE)) {
        db.createObjectStore(COPA_IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function lerConfigMMIDB(): Promise<ConfigMataMataPersist | null> {
  try {
    const db = await abrirCopaIDB();
    const data = await new Promise<unknown>((resolve, reject) => {
      const tx = db.transaction(COPA_IDB_STORE, 'readonly');
      const r = tx.objectStore(COPA_IDB_STORE).get(COPA_IDB_KEY_MM_CONFIG);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    db.close();
    if (!data || typeof data !== 'object') return null;
    return normalizarConfigMM(data as Partial<ConfigMataMataPersist>);
  } catch {
    return null;
  }
}

export async function salvarConfigMMRobusto(config: ConfigMataMataPersist): Promise<ConfigMataMataPersist> {
  const local = carregarConfigMMLocal();
  const idb = await lerConfigMMIDB();
  const mesclado = mesclarConfigMM(mesclarConfigMM(local, idb ?? configMMVazia()), config);

  salvarConfigMMLocal(mesclado);

  try {
    const db = await abrirCopaIDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(COPA_IDB_STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(COPA_IDB_STORE).put(mesclado, COPA_IDB_KEY_MM_CONFIG);
    });
    db.close();
  } catch {
    /* ignore */
  }

  return mesclado;
}

export async function restaurarConfigMM(): Promise<ConfigMataMataPersist> {
  const local = carregarConfigMMLocal();
  const idb = await lerConfigMMIDB();
  const mesclado = idb ? mesclarConfigMM(local, idb) : local;
  salvarConfigMMLocal(mesclado);
  try {
    const db = await abrirCopaIDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(COPA_IDB_STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(COPA_IDB_STORE).put(mesclado, COPA_IDB_KEY_MM_CONFIG);
    });
    db.close();
  } catch {
    /* ignore */
  }
  return mesclado;
}
